/*
 *  -----------------------------------------------------------------------
 *    <copyright file="home/study-summary.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation, useBoolean } from 'hooks';
import { HC_COLORS_MUTED, OpraColours } from 'interfaces/opra-chart.interface';
import HorizontalBarChart from 'views/components/core/charts/horizontal-bar-chart';
import PanelFrame from 'views/components/core/panel-frame';

import './home-styles.scss';
import ItemWrapper from '../core/item-wrapper';

const HomeStudySummary = ({ summary, user, studyInfo }: any) => {
    const { t } = useTranslation();

    const CATEGORIES = [t('home-myRisks'), t('home-myRiskObservations'), t('home-myRiskActions')];
    const isDataPresented = useBoolean(false);
    const isRAMActive = useBoolean(false);
    const [categories, setCategories] = useState([...CATEGORIES]);

    useEffect(() => {
        if (user && summary) {
            const isRAMActiveVal =
                user?.role?.rights.find(
                    (item: any) => item.moduleId === 2 && item.element.match(/page/) && item.permissions > 0,
                ) ?? false;
            if (isRAMActiveVal) {
                isRAMActive.setValue(!!isRAMActiveVal);
                let array = [...CATEGORIES];
                isDataPresented.setValue(
                    summary.numMyRisks + summary.numMyRiskActions + summary.numMyRiskObservations > 0,
                );
                setCategories([...array]);
            }
        }
    }, [user, summary]);

    const getPct = (numerator: number, denominator: number) => {
        const value = denominator ? (numerator * 100) / denominator : 0;
        return Math.round(value);
    };

    const getAmount = (key: string, custom: any) => {
        if (key === t('home-myRisks'))
            return {
                total: custom.amount,
                amount: custom.myRisks,
            };
        if (key === t('home-myRiskObservations'))
            return {
                total: custom.numMyRiskObservations,
                amount: custom.numRiskObservations,
            };
        return {
            total: custom.numMyRiskActions,
            amount: custom.numRiskActions,
        };
    };

    const formatTooltip = (key: string, percentage: number, seriesName: string, custom: any) => {
        const { total, amount } = getAmount(key, custom);

        return `Total: ${total} <br/> # ${seriesName}: ${amount} <br/> % ${seriesName}: ${Math.round(percentage)}`;
    };

    const isActiveStudyUser = studyInfo?.members.find((item: any) => item.userId === user?.id)?.active ?? false;

    const getValues = useCallback(
        (values: any[]) => {
            if (isRAMActive.value) return values;
            return [];
        },
        [isRAMActive.value],
    );

    return isActiveStudyUser ? (
        <>
            {isDataPresented.value ? (
                <PanelFrame title={summary.studyNum} headerColor="#fff" toggleLegend={false}>
                    {(getRef: any) => (
                        <HorizontalBarChart
                            getRef={getRef}
                            plotTitle={summary.studyNum}
                            hidePlotTitle
                            categories={categories}
                            xAxisTitle="Percentage %"
                            showLegend={false}
                            formatTooltip={formatTooltip}
                            maxValue={100}
                            series={[
                                {
                                    name: t('home-reviewOverdue'),
                                    color: HC_COLORS_MUTED[OpraColours.Red],
                                    values: getValues([getPct(summary.numMyOverdueRisks, summary.numMyRisks), 0, 0]),
                                    onClick: () => {},
                                    custom: {
                                        myRisks: summary.numMyOverdueRisks,
                                        amount: summary.numMyRisks,
                                    },
                                },
                                {
                                    name: t('home-reviewDue'),
                                    color: HC_COLORS_MUTED[OpraColours.Amber],
                                    values: getValues([getPct(summary.numMyReviewDueRisks, summary.numMyRisks), 0, 0]),
                                    onClick: () => {},
                                    custom: {
                                        myRisks: summary.numMyReviewDueRisks,
                                        amount: summary.numMyRisks,
                                    },
                                },
                                {
                                    name: t('home-reviewed'),
                                    color: HC_COLORS_MUTED[OpraColours.Green],
                                    values: getValues([getPct(summary.numMyReviewedRisks, summary.numMyRisks), 0, 0]),
                                    onClick: () => {},
                                    custom: {
                                        myRisks: summary.numMyReviewedRisks,
                                        amount: summary.numMyRisks,
                                    },
                                },
                                {
                                    name: t('home-notReviewed'),
                                    color: HC_COLORS_MUTED[OpraColours.LightGrey],
                                    values: getValues([
                                        getPct(summary.numMyNotReviewedRisks, summary.numMyRisks),
                                        0,
                                        0,
                                    ]),
                                    onClick: () => {},
                                    custom: {
                                        myRisks: summary.numMyNotReviewedRisks,
                                        amount: summary.numMyRisks,
                                    },
                                },
                                {
                                    name: t('home-Open'),
                                    color: HC_COLORS_MUTED[OpraColours.LightBlue],
                                    values: getValues([
                                        0,
                                        getPct(summary.numMyOpenRiskObservations, summary.numMyRiskObservations),
                                        getPct(summary.numMyOpenRiskActions, summary.numMyRiskActions),
                                    ]),
                                    custom: {
                                        numRiskObservations: summary.numMyOpenRiskObservations,
                                        numMyRiskObservations: summary.numMyRiskObservations,
                                        numRiskActions: summary.numMyOpenRiskActions,
                                        numMyRiskActions: summary.numMyRiskActions,
                                    },
                                    onClick: () => {},
                                },
                                {
                                    name: t('home-Closed'),
                                    color: HC_COLORS_MUTED[OpraColours.Orange],
                                    values: getValues([
                                        0,
                                        getPct(summary.numMyClosedRiskObservations, summary.numMyRiskObservations),
                                        getPct(summary.numMyClosedRiskActions, summary.numMyRiskActions),
                                    ]),
                                    custom: {
                                        numRiskObservations: summary.numMyClosedRiskObservations,
                                        numMyRiskObservations: summary.numMyRiskObservations,
                                        numRiskActions: summary.numMyClosedRiskActions,
                                        numMyRiskActions: summary.numMyRiskActions,
                                    },
                                    onClick: () => {},
                                },
                            ]}
                        />
                    )}
                </PanelFrame>
            ) : (
                <ItemWrapper
                    title={summary.studyNum}
                    customStyle={{ backgroundColor: '#fff' }}
                    customChildStyle={{ backgroundColor: '#fff' }}
                >
                    <p className="no-studies" style={{ margin: 15 }}>
                        {t('home:noDataToDisplay')}
                    </p>
                </ItemWrapper>
            )}
        </>
    ) : null;
};

export default React.memo(HomeStudySummary);
